import { useCallback, useRef, useState, type MouseEvent, type TouchEvent } from 'react';
import { fmt, type AdminRevenueSeriesPointDto } from '../api';

const H = 160;
const PAD_T = 10;
const PAD_B = 24;
/** Only until the first measurement lands; every real render uses the element's own width. */
const FALLBACK_W = 700;
/** Roughly what a "MM-DD" label needs before its neighbour starts crowding it. */
const LABEL_SLOT = 64;

/** SVG area/line chart with a hover crosshair — shared by the event Admin and platform SuperAdmin overview pages. */
export default function RevenueTrendChart({
  series, error,
}: { series: AdminRevenueSeriesPointDto[] | null; error: string }) {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  /*
   * The chart is drawn in the element's own pixels rather than a fixed 700-unit viewBox.
   *
   * A fixed viewBox scaled to fit meant every length in here was multiplied by an unknown: on a
   * 320px phone the 9.5px date labels landed at about 4px, and six of them were spaced for a canvas
   * more than twice the width they had, so they overlapped. Measuring first and drawing at 1:1
   * keeps a stated size a real size, and lets the label count be decided by how many actually fit.
   */
  const [width, setWidth] = useState(FALLBACK_W);
  const observer = useRef<ResizeObserver | null>(null);
  /*
   * A callback ref rather than an effect, because the element being measured does not exist on the
   * first render: `series` arrives from the network, so the first pass returns the loading line and
   * a mount-time effect would find nothing to observe and never look again.
   */
  const measureRef = useCallback((el: HTMLDivElement | null) => {
    observer.current?.disconnect();
    observer.current = null;
    if (!el) return;
    const measure = () => setWidth(Math.max(240, el.clientWidth || FALLBACK_W));
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    observer.current = ro;
  }, []);

  if (error) return <div className="form-error">{error}</div>;
  if (!series) return <div style={{ color: 'var(--muted)', fontSize: 14 }}>กำลังโหลด…</div>;
  if (series.length === 0) return <div style={{ color: 'var(--muted)', fontSize: 14 }}>ยังไม่มีข้อมูล</div>;

  const maxRevenue = Math.max(1, ...series.map((p) => p.revenue));
  const stepX = series.length > 1 ? width / (series.length - 1) : 0;
  const points = series.map((p, i) => ({ x: i * stepX, y: PAD_T + (1 - p.revenue / maxRevenue) * (H - PAD_T - PAD_B) }));
  const pathLine = points.map((pt, i) => `${i === 0 ? 'M' : 'L'}${pt.x.toFixed(1)},${pt.y.toFixed(1)}`).join(' ');
  const pathArea = `${pathLine} L${points[points.length - 1].x.toFixed(1)},${H - PAD_B} L0,${H - PAD_B} Z`;
  const totalRevenue = series.reduce((s, p) => s + p.revenue, 0);
  const labelEvery = Math.max(1, Math.ceil(series.length / Math.max(2, Math.floor(width / LABEL_SLOT))));
  const hovered = hoverIdx !== null ? series[hoverIdx] : null;
  const hoveredPoint = hoverIdx !== null ? points[hoverIdx] : null;
  const lastPoint = points[points.length - 1];

  const pickAt = (clientX: number, rect: DOMRect) => {
    const relX = clientX - rect.left;
    const idx = stepX > 0 ? Math.round(relX / stepX) : 0;
    setHoverIdx(Math.min(series.length - 1, Math.max(0, idx)));
  };
  const handleMove = (e: MouseEvent<SVGSVGElement>) => pickAt(e.clientX, e.currentTarget.getBoundingClientRect());
  /*
   * Organizers read this page on a phone, where there is no pointer to hover with — without a touch
   * handler the per-day readout under the chart was unreachable on the device most likely to be
   * asking for it. `touch-action: none` stops a drag along the chart from scrolling the page.
   */
  const handleTouch = (e: TouchEvent<SVGSVGElement>) => {
    const t = e.touches[0];
    if (t) pickAt(t.clientX, e.currentTarget.getBoundingClientRect());
  };

  return (
    <>
      <div style={{ fontSize: 12.5, color: 'var(--muted)', marginBottom: 12 }}>
        รวม {fmt(totalRevenue)} ใน {series.length.toLocaleString()} วัน
      </div>
      <div ref={measureRef}>
        <svg
          viewBox={`0 0 ${width} ${H}`}
          width="100%"
          height={H}
          style={{ display: 'block', touchAction: 'none', cursor: series.length > 1 ? 'crosshair' : 'default' }}
          onMouseMove={handleMove}
          onMouseLeave={() => setHoverIdx(null)}
          onTouchStart={handleTouch}
          onTouchMove={handleTouch}
        >
          <line x1="0" y1={H - PAD_B} x2={width} y2={H - PAD_B} stroke="var(--line)" strokeWidth="1" />
          <line x1="0" y1={PAD_T} x2={width} y2={PAD_T} stroke="var(--line)" strokeWidth="1" />
          <path d={pathArea} fill="var(--fg)" opacity="0.08" />
          <path d={pathLine} fill="none" stroke="var(--fg)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx={lastPoint.x} cy={lastPoint.y} r="3.5" fill="var(--fg)" />
          {hoveredPoint && (
            <>
              <line
                x1={hoveredPoint.x} y1={PAD_T} x2={hoveredPoint.x} y2={H - PAD_B}
                stroke="var(--border)" strokeWidth="1" strokeDasharray="3 3"
              />
              <circle cx={hoveredPoint.x} cy={hoveredPoint.y} r="4" fill="var(--fg)" />
            </>
          )}
          {series.map((p, i) => (i % labelEvery === 0 || i === series.length - 1) && (
            <text
              key={p.date}
              x={points[i].x}
              y={H - 6}
              fontSize="9.5"
              fill="var(--dim)"
              textAnchor={i === 0 ? 'start' : i === series.length - 1 ? 'end' : 'middle'}
              fontFamily="var(--mono)"
            >
              {p.date.slice(5)}
            </text>
          ))}
        </svg>
      </div>
      <div style={{ marginTop: 10, fontSize: 12.5, fontFamily: 'var(--mono)', color: hovered ? 'var(--fg)' : 'var(--dim)' }}>
        {hovered ? `${hovered.date} · ${fmt(hovered.revenue)} · ${hovered.orders} ออร์เดอร์` : 'แตะหรือลากบนกราฟเพื่อดูค่ารายวัน'}
      </div>
    </>
  );
}
